import { useCallback, useState } from 'react'

import _ from 'lodash'
import { useSetRecoilState } from 'recoil'
import { ItListItem } from 'types/graphql'

import { useFormContext } from '@redwoodjs/forms'

import { editorStatusState } from 'src/atoms/ui.atom'
import { MAX_LG_INPUT } from 'src/lib/constants'
import { useCurrentUser } from 'src/lib/hooks/auth/useCurrentUser'
import { isString } from 'src/lib/typeGuards'
import { isValidUrl } from 'src/lib/utils'
import { BrandColor } from 'src/types'

import { generateEmptyItem } from '../..'

type ParseOgResult = {
  title: string
  url: string
  imageUrl: string
}

const GET_OPEN_GRAPH_TIMEOUT = 30000

const getOg = async (
  url: string,
  itListItemId: number
): Promise<{ data: ParseOgResult } | null> => {
  try {
    const response = await fetch(
      `${window.RWJS_API_URL}/getOg?${new URLSearchParams({
        url,
        id: `${itListItemId}`,
      })}`,
      {
        signal: AbortSignal.timeout(GET_OPEN_GRAPH_TIMEOUT),
      }
    )

    return response.json()
  } catch (err) {
    return null
  }
}

export type UseScraperEventReturn = {
  creatorBrandColor: BrandColor
  load: boolean
  hasRetrievedInfo: boolean
  onClick: (url: string) => void
  hasError: boolean
}

export default function useScraper(
  item: ItListItem,
  onBeforeUpdate: (item: ItListItem) => ItListItem
): UseScraperEventReturn {
  const {
    formState: { isSubmitted },
    reset,
  } = useFormContext<ItListItem>()
  const { brandColor: creatorBrandColor } = useCurrentUser()
  const [load, setLoad] = useState<boolean>(false)
  const [hasRetrievedInfo, setHasRetrievedInfo] = useState(false)
  const setEditorStatus = useSetRecoilState(editorStatusState)

  const initializeItemWithOpenGraph = useCallback(
    async (url: string) => {
      setLoad(true)
      setEditorStatus('saving')

      const response = await getOg(url, item.id)

      /** @warning Undefined fields should be cast as null to prevent cloning previous item. */
      const {
        url: urlFromScraper,
        imageUrl = null,
        title = null,
      } = response?.data || {}

      let newItem: ItListItem = {
        ...generateEmptyItem({
          id: item.id,
          type: item.type,
          url: urlFromScraper,
          [item.type]: {
            id: item[item.type]?.id,
            // scraper event uses signed url because the url isn't in the database. Only to show the image in the form
            coverUrl: imageUrl,
          },
        }),
      }

      if (isString(title)) {
        newItem.title =
          title.length > MAX_LG_INPUT
            ? `${title.substring(0, MAX_LG_INPUT - 3)}...`
            : title
      }

      setHasRetrievedInfo(true)
      setLoad(false)
      if (onBeforeUpdate) {
        newItem = onBeforeUpdate(newItem)
      }
      reset(newItem)
    },
    [setEditorStatus, item, onBeforeUpdate, reset]
  )

  const onClick = React.useCallback(
    (url: string) => {
      if (isValidUrl(url)) initializeItemWithOpenGraph(url)
    },
    [initializeItemWithOpenGraph]
  )
  return {
    creatorBrandColor,
    load,
    onClick,
    hasRetrievedInfo,
    hasError: isSubmitted,
  }
}
